/**
 * filterStylesResolver.js
 *
 * Хелпер для получения items фильтров из layerStyles
 * Позволяет избежать дублирования данных между layerStyles и filterStyles конфигами
 *
 * Использование:
 * const items = getStyleFilterItems(PARKING_ALERTS_STYLE, 'statuses');
 */

/**
 * Получает items из стиля слоя по ключу
 * Поддерживает оба формата:
 * 1. Новый (рекомендуется): style[key] = [] (массив напрямую)
 * 2. Старый (совместимость): style[key] = { filterItems: [], legendItems: [] }
 *
 * @param {object} style - Объект стиля слоя (например, PARKING_ALERTS_STYLE)
 * @param {string} styleKey - Ключ в стиле (например, 'statuses', 'errors')
 * @returns {array} Массив items для фильтра
 *
 * @example
 * const items = getStyleFilterItems(PARKING_ALERTS_STYLE, 'statuses');
 * // Вернёт: PARKING_ALERTS_STYLE.statuses (если массив) или PARKING_ALERTS_STYLE.statuses.filterItems (если объект)
 */
export const getStyleFilterItems = (style, styleKey) => {
    if (!style || !styleKey) {
        return [];
    }

    const styleSection = style[styleKey];
    if (!styleSection) {
        console.warn(`Style section "${styleKey}" not found in provided style`);
        return [];
    }

    // ✅ Новый формат: массив напрямую
    if (Array.isArray(styleSection)) {
        return styleSection;
    }

    // ✅ Старый формат: объект с filterItems или legendItems
    if (styleSection.filterItems) {
        return styleSection.filterItems;
    }

    if (styleSection.legendItems) {
        return styleSection.legendItems;
    }

    console.warn(`No items found in style["${styleKey}"] (expected array, filterItems, or legendItems)`);
    return [];
};

/**
 * Получает items и трансформирует их если нужно
 * Полезно если фильтру нужны поля из нескольких мест
 *
 * @param {object} style - Объект стиля слоя
 * @param {string} styleKey - Ключ в стиле
 * @param {function} transformer - Опциональная функция трансформации items
 * @returns {array} Трансформированный массив items
 *
 * @example
 * const items = getStyleFilterItemsTransformed(
 *   PARKING_ALERTS_STYLE,
 *   'statuses',
 *   items => items.map(item => ({ ...item, type: 'alert' }))
 * );
 */
export const getStyleFilterItemsTransformed = (style, styleKey, transformer) => {
    const items = getStyleFilterItems(style, styleKey);

    if (!transformer || typeof transformer !== 'function') {
        return items;
    }

    return transformer(items);
};
